import { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { getEmitterTypeToEnum } from '@semios/app-platform-values-helpers-and-types'
import type { TIrrigationDeviceStatus, TValuesCurrentIrrigationZones } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import type { TValidIrrigationSchedulerZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { captureFrontendError } from 'utils/captureFrontendError'
import { showNotification } from 'utils/showNotification'
import { irrigationSchedulerCurrentStatus } from '../../api/irrigationSchedulerCurrentStatus'
import { irrigationSchedulerCurrentValues } from '../../api/irrigationSchedulerCurrentValues'
import { irrigationSchedulerEventsGet } from '../../api/irrigationSchedulerEventsGet'
import { isValidIrrigationSchedulerZoneEmitterId } from '../../isValidIrrigationSchedulerZoneEmitterId'
import type { fieldAssetAndIrrigationSchedulerSelector } from '../../useFetchEventsAndCurrentStatusListener'
import type { ISOString } from '../irrigationSchedulerStore'
import { getVisibleIrrigationZoneEmitters } from '../selectors/getVisibleIrrigationZoneEmitters'
import { actions } from './actions'

export const fetchAllIrrigationSchedulerEventsAndCurrentValuesData = async (
  selection: ReturnType<typeof fieldAssetAndIrrigationSchedulerSelector>,
  setLoadingEventsAndCurrentStatus: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const visibleIrrigationZoneEmitters = getVisibleIrrigationZoneEmitters()

  if (visibleIrrigationZoneEmitters.length) {
    setLoadingEventsAndCurrentStatus(true)

    const irrigationZoneIds = Array.from(
      new Set(
        visibleIrrigationZoneEmitters.map((irrigationZoneEmitter) =>
          Number(irrigationZoneEmitter.irrigationZoneId),
        ),
      ),
    )

    const results = await Promise.allSettled([
      irrigationSchedulerEventsGet({
        dateFrom: selection.dateRange.mDateFrom.toISOString(),
        dateTo: selection.dateRange.mDateTo.toISOString(),
        irrigationZoneIds,
      }),
      irrigationSchedulerCurrentStatus({ irrigationZoneIds }),
      irrigationSchedulerCurrentValues(selection.selectedPropertyId),
    ])

    const [eventsResult, currentStatusResult, currentValuesResult] = results

    const stoppedIrrigationZoneEmitters: Array<{
      irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId
      timestamp: ISOString
    }> = []

    const currentEmitterStatus: Array<{
      irrigationZoneEmitterId: TValidIrrigationSchedulerZoneEmitterId
      currentEmitterStatus: TIrrigationDeviceStatus
    }> = []

    if (eventsResult.status === 'fulfilled') {
      const events = eventsResult.value

      events.forEach((event) => {
        if (!isValidIrrigationSchedulerZoneEmitterId(event.irrigationZoneEmitterId)) {
          // eslint-disable-next-line no-console
          console.warn(`Invalid irrigationZoneEmitterId: ${event.irrigationZoneEmitterId}`)

          return
        }

        let currentValueStatus: TIrrigationDeviceStatus | null = null

        if (currentValuesResult.status === 'fulfilled') {
          const currentValuesForSensor = currentValuesResult.value.irrigation_zones?.[event.irrigationZoneId]

          if (currentValuesForSensor) {
            const emitterType = getEmitterTypeToEnum(event.emitterType)
            const currentValueKey = `irrigationIsOn_${emitterType}_status` as TValuesCurrentIrrigationZones

            currentValueStatus = (currentValuesForSensor[currentValueKey]?.value ??
              null) as TIrrigationDeviceStatus
          }
        }

        currentEmitterStatus.push({
          irrigationZoneEmitterId: event.irrigationZoneEmitterId,
          currentEmitterStatus: currentValueStatus,
        })
      })

      actions.api.eventsGet(events)

      actions.main.setEmitterStatus(currentEmitterStatus)
    } else {
      captureFrontendError(eventsResult.reason)
    }

    if (currentStatusResult.status === 'fulfilled') {
      currentStatusResult.value.forEach((item) => {
        if (!isValidIrrigationSchedulerZoneEmitterId(item.irrigationZoneEmitterId)) {
          // eslint-disable-next-line no-console
          console.warn(`Invalid irrigationZoneEmitterId: ${item.irrigationZoneEmitterId}`)

          return
        }

        if (
          item.stopStatus &&
          (item.stopStatus.status ===
            SharedTypes.IrrigationScheduler.zoneStopStatusSchema.enum.STOP_PENDING ||
            item.stopStatus.status === SharedTypes.IrrigationScheduler.zoneStopStatusSchema.enum.STOPPED)
        ) {
          stoppedIrrigationZoneEmitters.push({
            irrigationZoneEmitterId: item.irrigationZoneEmitterId,
            timestamp: item.stopStatus.setAt,
          })
        }
      })

      actions.main.setStoppedIrrigationZoneEmitters(stoppedIrrigationZoneEmitters)
    } else {
      captureFrontendError(currentStatusResult.reason)
    }

    if (currentValuesResult.status === 'rejected') {
      captureFrontendError(currentValuesResult.reason)
    }

    if (results.some((result) => result.status === 'rejected')) {
      showNotification({ type: 'error', message: translate.phrases.banyanApp('There was an error') })
    }

    setLoadingEventsAndCurrentStatus(false)
  }
}
